import React, { useMemo, useState } from 'react';

import { ScopedTheme } from '@yandex-360/orb-components/dist/components/Theme';
import { useSelector } from 'react-redux';

import { Content, Reason } from '../../../../components/feedback-form-content';
import { ModalConfirmPure } from '../../../../components/modal-confirm';
import { i18n } from '../../../../helpers/i18n';
import { useActions } from '../../../../hooks';
import { selectIsFeedbackFormVisible } from '../../selectors';
import { setFeedbackComponentVisibility, submitFeedback } from '../../slice';
import { Stars } from '../stars';
import styles from './styles.css';

export const FeedbackModal = () => {
    const isVisible = useSelector(selectIsFeedbackFormVisible);

    const [rating, setRating] = useState<number>();
    const [message, setMessage] = useState<string>('');

    const { checkedReasons, addReason, deleteReason } = useMemo(() => {
        const checkedReasons = new Set<Reason>();

        return {
            checkedReasons,
            addReason: (reason: Reason) => checkedReasons.add(reason),
            deleteReason: (reason: Reason) => checkedReasons.delete(reason),
        };
    }, []);

    const onMessageChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setMessage(event.target.value);
    };

    const actions = useActions({ setFeedbackComponentVisibility, submitFeedback });

    const onSubmit = () => {
        const additionalData = rating && rating < 3 ? { comment: message, reasons: [...checkedReasons] } : {};

        actions.submitFeedback({ rating, ...additionalData });
        onClose();
    };

    const onClose = () => {
        setRating(undefined);
        setMessage('');
        checkedReasons.clear();

        actions.setFeedbackComponentVisibility(false);
    };

    const showContent = Boolean(rating && rating < 4);

    return (
        <ScopedTheme theme="dark">
            <ModalConfirmPure
                visible={isVisible}
                onClose={onClose}
                onCancel={onClose}
                onConfirm={onSubmit}
                title={i18n('feedback', 'feedback-form-header')}
                confirmLabel={i18n('feedback', 'submit')}
                testId="feedback-modal"
            >
                <div className={styles.container}>
                    <Stars onRatingChange={setRating} />
                    {showContent && (
                        <Content
                            message={message}
                            onMessageChange={onMessageChange}
                            checkedReasons={checkedReasons}
                            addReason={addReason}
                            deleteReason={deleteReason}
                        />
                    )}
                </div>
            </ModalConfirmPure>
        </ScopedTheme>
    );
};
